import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { Institution } from '../entities/institution.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import { mailer } from '../mails/communication.js';
import { buildBookableWorkshopsHTML } from '../mails/mail-helpers.js';
import { getNotificationType } from './_helper.js';

defineMailTemplate(MailTemplateType.INFO_NEWSLETTER, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Weitere Workshops',
    content: `<p>Hallo</p>
          folgende Workshops können noch besucht werden:
<p>{{bookable-workshops}}</p>

<p>Wir w&uuml;nschen Ihnen viel Freude und Erkenntnisgewinn in den Veranstaltungen. Das ist unser Ansporn und Anspruch! Kontaktieren Sie uns gern <a href="mailto:contact@anmeldung.uni-frankfurt.de">per Mail</a>, wenn Sie ein Anliegen (Fragen, Lob &amp; Kritik) haben.</p>
<p>Viele Gr&uuml;&szlig;e</p>
`,
    footer: '',
  },
  variables: InstitutionMailTemplate.getAvailableVariables(MailTemplateType.INFO_NEWSLETTER),
  attachments: [],
  displayName: 'Newsletter',
  editorHint: () => InstitutionMailTemplate.getMailHint(MailTemplateType.INFO_NEWSLETTER),
});

export async function notifyNewWorkshops(workshopIDs: string[], userID: string) {
  const mailTemplate = (await InstitutionMailTemplate.getMailTemplateForInstitution(
    '',
    MailTemplateType.INFO_NEWSLETTER,
  )(InstitutionMailTemplate.allFields)) as InstitutionMailTemplate;

  const workshops = await getByID(
    Workshop,
    workshopIDs,
  )({
    _id: 1,
    institutionID: 1,
    name: 1,
    language: 1,
    appointments: { name: 1, start: 1, end: 1, location: 1 },
  });

  const user = await getByID(
    User,
    userID,
  )({
    _id: 1,
    title: 1,
    firstName: 1,
    lastName: 1,
    email: 1,
    whatsappNotification: 1,
    whatsappPhone: 1,
    emailConfirmed: 1,
  });
  if (!user) throw new Error('user not found ' + userID);

  const token = await generateLoginToken({
    userID: user._id,
    userClass: 'User',
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const mailVars = {
    [MailTemplateVars.TITLE]: user.title ?? '',
    [MailTemplateVars.FIRST_NAME]: user.firstName,
    [MailTemplateVars.LAST_NAME]: user.lastName,
    [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
    [MailTemplateVars.BOOKABLE_WORKSHOPS]: buildBookableWorkshopsHTML(workshops, false),
    [MailTemplateVars.UNSUBSCRIBE_FROM_NEWSLETTER_LINK]: `<a
      href="${A.absoluteUrl('/profile')}" 
      target="_blank"
    > Benachrichtigungseinstellungen anpassen </a>`,
  };

  // Get institution with color definitions if there are any workshops
  if (workshops.length > 0) {
    // Use the first workshop's institution ID
    const institutionID = workshops[0].institutionID;

    if (institutionID) {
      const institution = await getByID(
        Institution,
        institutionID,
      )({
        _id: 1,
        name: 1,
        colorDefintions: selectionSet(() => ColorDefinitions, 4),
        settings: {
          colorDefinitions: selectionSet(() => ColorDefinitions, 4),
          inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
        },
      });

      // Set mail theme
      if (institution) {
        bhBaseQlContext.colorDefinitions =
          institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;
      }
    }
  }

  const notificationType = getNotificationType(user, mailTemplate);
  if (notificationType === 'email') {
    const content = await mailTemplate.buildEmail(mailVars as any);
    const subject = await mailTemplate.buildSubject(mailVars as any);

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    const recipient = new Recipient(user.email);
    const message = await Message.compose({
      subject,
      html: content,
    });

    try {
      const _mailer = await mailer;
      if (!_mailer) throw new Error('could not get mailer');
      await _mailer.sendMail(message, recipient);
    } catch (err) {
      logger.error({ email: user.email, err }, 'E-Mail Konnte nicht verschickt werden; 8373');
    }
  }
}
